import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_Updater,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo } from 'react';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { useUnassociatedDevicesInCurrentOrganisation } from '@/hooks/use-devices';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { composeTableOptions } from '@/table';
import { DeviceAssignedToOrganisation } from '@/types/device';
import { NewUnitWizardFooter } from '../new-unit-wizard-footer';

const columns: MRT_ColumnDef<DeviceAssignedToOrganisation>[] = [
  {
    accessorKey: 'friendlyName',
    header: 'Device Name',
    grow: true,
    size: 150,
  },
  {
    accessorKey: 'serialNumber',
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    accessorKey: 'model',
    header: 'Model',
    size: 150,
  },
  {
    id: 'status',
    accessorFn: ({ connectivity }) => connectivity?.status,
    header: 'Status',
    grow: false,
    size: 100,
    Cell: ({ row }) => {
      const connectivityStatus = row.original.connectivity?.status ?? 'Unknown';
      return (
        <StatusBadge
          text={connectivityStatus}
          variant={connectivityStatus === 'Online' ? 'success' : 'alert'}
        />
      );
    },
  },
];

export type AssociateDevicesStepProps = {
  selectedDevices: DeviceAssignedToOrganisation[];
  onSelectedDevicesChange: (
    selectedDevices: DeviceAssignedToOrganisation[],
  ) => void;
};

export const AssociateDevicesStep: FC<AssociateDevicesStepProps> = ({
  selectedDevices,
  onSelectedDevicesChange,
}) => {
  const { data: devices, isLoading: isLoadingDevices } =
    useUnassociatedDevicesInCurrentOrganisation();

  const rowSelection = useMemo(
    () =>
      selectedDevices.reduce((state, device) => {
        const rowIndex = devices.findIndex(
          ({ endpointId }) => endpointId === device.endpointId,
        );
        if (rowIndex > -1) {
          return {
            ...state,
            [rowIndex]: true,
          };
        }
        return state;
      }, {}),
    [devices, selectedDevices],
  );

  const handleRowSelectionChange = (
    rowSelectionUpdater: MRT_Updater<MRT_RowSelectionState>,
  ) => {
    const newRowSelection =
      typeof rowSelectionUpdater === 'function'
        ? rowSelectionUpdater(rowSelection)
        : rowSelectionUpdater;
    const selectedDevices: DeviceAssignedToOrganisation[] = (
      Object.keys(newRowSelection) as unknown[] as number[]
    ).map((rowIndex) => devices[rowIndex]);

    onSelectedDevicesChange(selectedDevices);
  };

  const table = useMaterialReactTable({
    ...composeTableOptions<DeviceAssignedToOrganisation>({
      enableRowSelection: true,
    }),
    columns,
    enableColumnResizing: true,
    data: devices,
    state: {
      isLoading: isLoadingDevices,
      rowSelection,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: handleRowSelectionChange,
  });

  return (
    <>
      <SectionContainerLayout className="m-0 border-none p-0">
        <SectionHeadingGroup
          subtitle="Select the devices you wish to associate with this unit from the
          available device list. You can skip this step and add devices later if
          needed."
          title="Associate device(s)"
        />
        <div className="pt-8">
          <MaterialReactTable table={table} />
        </div>
      </SectionContainerLayout>
      <NewUnitWizardFooter skipable />
    </>
  );
};
